import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faClock } from '@fortawesome/pro-duotone-svg-icons/faClock';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { RoomSchedule, scheduleStatus } from '../../../models/schedule';
import { recurrenceLabel } from '../../../helpers/schedule-label';
import { LocalizedDatePipe } from '../../../pipes/localized-date.pipe';

/**
 * Compact, read-only display of a room's meeting schedule: whether it is in
 * progress, when it next starts, plus a repeat hint. Used on the Room Login and
 * Preparation Area whenever a scheduled room is selected. Pure presentation — the
 * status is derived from `scheduleStatus` and refreshed on a coarse timer so
 * "upcoming" rolls over to "in progress" without a page interaction.
 */
@Component({
  selector: 'andor-schedule-display',
  standalone: true,
  imports: [LocalizedDatePipe, TranslatePipe, FaIconComponent],
  templateUrl: './schedule-display.html',
  styleUrl: './schedule-display.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleDisplay {
  readonly schedule = input.required<RoomSchedule>();

  protected readonly faClock = faClock;
  readonly #translate = inject(TranslateService);

  private readonly _now = signal(Date.now());
  protected readonly status = computed(() => scheduleStatus(this.schedule(), this._now()));

  protected readonly repeatLabel = computed(() => {
    const rec = this.schedule().recurrence;
    return rec ? recurrenceLabel(rec, this.#translate) : '';
  });

  constructor() {
    const id = setInterval(() => this._now.set(Date.now()), 30_000);
    inject(DestroyRef).onDestroy(() => clearInterval(id));
  }
}
